import React, { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { getPaymentOrder } from "../api/paymentApi";
import { useAuth } from "../context/AuthContext";
import { trackPaymentEvent } from "../utils/analytics";
import "./PaymentReturn.css";

const SUPPORT_EMAIL = "support@tajnemysli.com";
const POLL_WINDOW_MS = 90000;
const POLL_INTERVAL_MS = 1500;
const TERMINAL_STATUSES = new Set(["CREDITED", "FAILED", "EXPIRED"]);

function readReturnTo() {
  try {
    const stored = sessionStorage.getItem("paymentReturnTo");
    // Only allow same-origin app paths, never a full URL from another host.
    if (stored && stored.startsWith("/") && !stored.startsWith("//") && !stored.startsWith("/payment/return")) {
      return stored;
    }
  } catch (e) {
    /* sessionStorage unavailable (private mode) */
  }
  return "/profile";
}

function PaymentReturn() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const { refreshUser } = useAuth();
  const [status, setStatus] = useState("LOADING");
  const [order, setOrder] = useState(null);
  const [errorMessage, setErrorMessage] = useState("");
  const [retryCount, setRetryCount] = useState(0);
  const [returnTo] = useState(readReturnTo);

  useEffect(() => {
    const id = params.get("order_id") || params.get("session_id");
    if (!id) {
      setStatus("FAILED");
      return;
    }
    let stopped = false;
    let timer;
    let checking = false;
    const started = Date.now();

    function stopTimer() {
      if (timer) {
        clearTimeout(timer);
        timer = undefined;
      }
    }

    function setTerminalStatus(data) {
      const s = data?.status;
      setOrder(data);
      setStatus(s);
      setErrorMessage("");
      trackPaymentEvent(s === "CREDITED" ? "succeeded" : "failed", {
        packCode: data?.packCode,
        value: Number(data?.priceAmountMinor || 0) / 100,
        currency: data?.currency
      });
      if (s === "CREDITED") void refreshUser();
    }

    function handleRequestError(error) {
      const responseStatus = error?.response?.status;
      if (responseStatus === 401 || responseStatus === 403) {
        setStatus("ERROR");
        setErrorMessage("Your session has expired. Please sign in again and check your payment history.");
        return "fatal";
      }
      if (responseStatus === 404) {
        setStatus("ERROR");
        setErrorMessage("We could not find this payment. Please check your payment history or contact support.");
        return "fatal";
      }
      if (responseStatus && responseStatus >= 400 && responseStatus < 500) {
        setStatus("ERROR");
        setErrorMessage("We could not confirm this payment. Please try again or contact support.");
        return "fatal";
      }

      setErrorMessage("We are having trouble checking your payment. We will keep trying.");
      return "transient";
    }

    async function poll(finalAttempt = false) {
      if (stopped || checking) return;
      checking = true;
      try {
        const { data } = await getPaymentOrder(id);
        if (stopped) return;
        const s = data?.status;
        if (TERMINAL_STATUSES.has(s)) {
          stopTimer();
          setTerminalStatus(data);
          return;
        }
        setErrorMessage("");
        if (finalAttempt || Date.now() - started >= POLL_WINDOW_MS) {
          stopTimer();
          setStatus("TIMEOUT");
          trackPaymentEvent("failed");
          return;
        }
      } catch (e) {
        if (stopped) return;
        const result = handleRequestError(e);
        if (result === "fatal") {
          stopTimer();
          return;
        }
        if (finalAttempt) {
          stopTimer();
          setStatus("TIMEOUT");
          trackPaymentEvent("failed");
          return;
        }
      } finally {
        checking = false;
      }

      if (!stopped) {
        const elapsed = Date.now() - started;
        timer = setTimeout(() => poll(elapsed >= POLL_WINDOW_MS), elapsed >= POLL_WINDOW_MS ? 0 : POLL_INTERVAL_MS);
      }
    }

    poll();
    function handleVisibilityChange() {
      if (document.visibilityState === "visible") {
        stopTimer();
        poll();
      }
    }
    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      stopped = true;
      stopTimer();
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [params, refreshUser, retryCount]);

  function retryPaymentCheck() {
    setStatus("LOADING");
    setOrder(null);
    setErrorMessage("");
    setRetryCount((count) => count + 1);
  }

  function goBack() {
    try {
      sessionStorage.removeItem("paymentReturnTo");
    } catch (e) {
      /* ignore */
    }
    navigate(returnTo);
  }

  const isMatchRefill = order?.packCode === "MATCH_REFILL";
  const grantedUnits = Number(order?.unitsGranted || 0);

  const views = {
    LOADING: {
      icon: <div className="payment-return-spinner" aria-hidden="true" />,
      title: "Processing your payment",
      body: "Please wait while we confirm your payment. This usually takes a few seconds — do not close this page.",
    },
    CREDITED: {
      icon: <div className="payment-return-icon success" aria-hidden="true">✓</div>,
      title: "Payment successful",
      body: "Thank you for your purchase!",
    },
    TIMEOUT: {
      icon: <div className="payment-return-spinner" aria-hidden="true" />,
      title: "Payment processing",
      body: "Your payment is taking longer than usual. Units will appear on your account within a few minutes — no further action is needed.",
    },
    FAILED: {
      icon: <div className="payment-return-icon error" aria-hidden="true">!</div>,
      title: "Payment failed",
      body: "No units were added and you have not been charged. Please try again.",
    },
    EXPIRED: {
      icon: <div className="payment-return-icon error" aria-hidden="true">!</div>,
      title: "Payment expired",
      body: "This checkout session expired before payment was completed. Please try again.",
    },
    ERROR: {
      icon: <div className="payment-return-icon error" aria-hidden="true">!</div>,
      title: "Unable to confirm payment",
      body: errorMessage || "We could not confirm the payment status.",
    },
  };

  const view = views[status] || views.LOADING;
  const isPending = status === "LOADING" || status === "TIMEOUT";

  return (
    <div className="payment-return-overlay">
      <div className="payment-return-card" role="dialog" aria-modal="true" aria-live="polite" aria-busy={isPending}>
        {view.icon}
        <h1 className="payment-return-title">{view.title}</h1>
        <p className="payment-return-body">{view.body}</p>

        {status === "CREDITED" && grantedUnits > 0 && (
          <div className="payment-return-units">
            {isMatchRefill ? (
              <>
                Added to your daily limit: <strong>+{grantedUnits}</strong> matches
              </>
            ) : (
              <>
                Credited to your account: <strong>{grantedUnits}</strong> units
              </>
            )}
          </div>
        )}

        <div className="payment-return-actions">
          {(status === "TIMEOUT" || status === "ERROR") && (
            <button type="button" className="payment-return-primary" onClick={retryPaymentCheck}>
              Check again
            </button>
          )}
          <button type="button" className="payment-return-primary" onClick={goBack}>
            Continue
          </button>
          {status !== "CREDITED" && (
            <a
              className="payment-return-secondary"
              href={`mailto:${SUPPORT_EMAIL}?subject=${encodeURIComponent("Payment issue")}`}
            >
              Contact support
            </a>
          )}
        </div>
      </div>
    </div>
  );
}

export default PaymentReturn;
